// Empty State — composable, centered IconStack illustration for empty views.
// Compose the pieces you need; add buttons, links, or anything else as children.
//
//   <EmptyState className="m-auto">
//     <EmptyState.Icon><ClipboardList className="size-4" /></EmptyState.Icon>
//     <EmptyState.Content>
//       <EmptyState.Title>No care plans yet</EmptyState.Title>
//       <EmptyState.Description>…</EmptyState.Description>
//     </EmptyState.Content>
//     <EmptyState.Actions>
//       <Button>Add plan</Button>
//     </EmptyState.Actions>
//   </EmptyState>
//
// Placement (fill / centering) is the caller's concern — pass `className`
// (e.g. `m-auto` inside a flex-1 column) to position the block.

import { IconStack } from "@/components/reui/icon-stack";
import { cn } from "@/lib/utils";

/** Centered column that lays out its children with a consistent vertical rhythm. */
function Root({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col items-center gap-4 text-center", className)} {...props} />;
}

/** Layered isometric illustration. Children render as the glyph inside the stack. */
function Icon({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div className={className} {...props}>
      <IconStack>{children}</IconStack>
    </div>
  );
}

/** Tight text group — pairs a Title with an optional Description. */
function Content({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("space-y-1", className)} {...props} />;
}

function Title({ className, ...props }: React.ComponentProps<"p">) {
  return <p className={cn("text-base font-[550]", className)} {...props} />;
}

function Description({ className, ...props }: React.ComponentProps<"p">) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

/** Row for calls to action (buttons, links). */
function Actions({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex items-center justify-center gap-2", className)} {...props} />;
}

export const EmptyState = Object.assign(Root, {
  Icon,
  Content,
  Title,
  Description,
  Actions,
});
